import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronRight, Send, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "../../lib/utils";
import type { GraphRunEvent } from "../../hooks/useGraphRun";
import { StateInspector } from "./StateInspector";

interface InteractionPanelProps {
  events: GraphRunEvent[];
  status: "idle" | "running" | "succeeded" | "failed" | "interrupted";
  error: string | null;
  onSubmit: (message: string) => void;
  onStop: () => void;
  nodes: Array<{ id: string; data: Record<string, unknown> }>;
}

function EventSection({ event, index }: { event: GraphRunEvent; index: number }) {
  const [expanded, setExpanded] = useState(true);
  const nodeId = event.nodeId ?? "unknown";
  const nodeName = (event.data?.nodeName as string) ?? nodeId;
  const timestamp = event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : "";

  const typeStyles: Record<string, string> = {
    node_start: "text-blue-400",
    node_end: "text-emerald-400",
    stream: "text-foreground/70",
    error: "text-red-400",
    interrupt: "text-orange-400",
    complete: "text-emerald-500",
    done: "text-emerald-500",
  };

  return (
    <div className="border-b border-border/30 last:border-0">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs hover:bg-accent/30 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
        <span className={cn("font-mono text-[11px]", typeStyles[event.type] ?? "text-muted-foreground")}>
          {event.type}
        </span>
        <span className="text-foreground/80 font-medium truncate">{nodeName}</span>
        <span className="ml-auto text-[10px] text-muted-foreground tabular-nums shrink-0">{timestamp}</span>
      </button>
      {expanded && event.data && (
        <div className="px-3 pb-2 ml-5">
          {event.type === "stream" && event.data.content ? (
            <div className="text-xs text-foreground/80 whitespace-pre-wrap leading-relaxed">
              {String(event.data.content)}
            </div>
          ) : event.type === "error" ? (
            <div className="text-xs text-red-400 font-mono">{String(event.data.message ?? JSON.stringify(event.data))}</div>
          ) : (
            <ToolCallDisplay data={event.data} />
          )}
        </div>
      )}
    </div>
  );
}

function ToolCallDisplay({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([k]) => k !== "nodeName" && k !== "runId");
  if (entries.length === 0) return null;

  return (
    <div className="rounded-md border border-border/50 bg-background/50 overflow-hidden">
      <table className="w-full text-xs">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key} className="border-b border-border/30 last:border-0">
              <td className="px-2.5 py-1.5 text-muted-foreground font-mono whitespace-nowrap align-top w-24">
                {key}
              </td>
              <td className="px-2.5 py-1.5 text-foreground/80 font-mono break-all">
                {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function InteractionPanel({ events, status, error, onSubmit, onStop, nodes }: InteractionPanelProps) {
  const [message, setMessage] = useState("");
  const [asNode, setAsNode] = useState("__start__");
  const [showRaw, setShowRaw] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events.length]);

  const handleSubmit = () => {
    if (!message.trim()) return;
    onSubmit(message.trim());
    setMessage("");
  };

  return (
    <div className="flex flex-col h-full bg-card/30">
      {/* Thread header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50 shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Trace</span>
        <div className="ml-auto flex items-center gap-1.5">
          {status === "running" && (
            <span className="flex items-center gap-1.5 text-[10px] text-blue-400">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
              </span>
              Running
            </span>
          )}
          {status === "succeeded" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500">Complete</span>
          )}
          {status === "failed" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-500">Failed</span>
          )}
          {status === "interrupted" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">Awaiting review</span>
          )}
        </div>
      </div>

      {/* Events list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-auto-hide min-h-0">
        {events.length === 0 && status === "idle" && (
          <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
            Run an agent to see the execution trace
          </div>
        )}
        {events.map((event, i) => (
          <EventSection key={i} event={event} index={i} />
        ))}
        {error && (
          <div className="px-3 py-2 text-xs text-red-400 font-mono">
            Error: {error}
          </div>
        )}
      </div>

      {/* State Inspector */}
      {events.length > 0 && <StateInspector events={events} />}

      {/* Input section */}
      <div className="border-t border-border/50 shrink-0">
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border/30">
          <span className="text-[10px] font-medium text-muted-foreground">Input</span>
          <button
            type="button"
            onClick={() => setShowRaw(!showRaw)}
            className={cn(
              "text-[10px] px-1.5 py-0.5 rounded border transition-colors",
              showRaw ? "border-primary/50 text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"
            )}
          >
            <Eye className="h-3 w-3 inline mr-1" />
            View Raw
          </button>
        </div>
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-muted-foreground shrink-0">As Node</label>
            <select
              value={asNode}
              onChange={(e) => setAsNode(e.target.value)}
              className="flex-1 text-xs rounded border border-border bg-background px-2 py-1 focus:border-primary focus:outline-none"
            >
              <option value="__start__">__start__</option>
              {nodes.map((n) => (
                <option key={n.id} value={n.id}>
                  {(n.data.name as string) ?? n.id}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder="Type a message..."
              rows={2}
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-xs resize-none focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <div className="flex flex-col gap-1.5">
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={!message.trim() || status === "running"}
                className="h-8"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
              {status === "running" && (
                <Button size="sm" variant="outline" onClick={onStop} className="h-8 text-[10px]">
                  Stop
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
